import type { GraphQLTaggedNode } from 'relay-runtime';
import { graphql } from 'relay-runtime';

import { keys as sessionStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { type UseQueryInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

// eslint-disable-next-line import/no-cycle
import { PopularFeedTimeGranularityValue } from './Feed.tsx';
import { useFeedDropdownStorage } from './useFeedDropdownStorage.ts';
import { PopularFeedWithTimeGranularityStorageSnippetsV2Query } from './PopularFeedWithTimeGranularityStorageSnippetsV2.graphql.ts';
import type {
	PopularFeedWithTimeGranularityStorageSnippetsV2Query$data,
	PopularFeedWithTimeGranularityStorageSnippetsV2Query$variables,
} from './__generated__/PopularFeedWithTimeGranularityStorageSnippetsV2Query.graphql.ts';
import type {
	usePopularFeedWithTimeGranularityStorageQuery$data,
	usePopularFeedWithTimeGranularityStorageQuery$variables,
} from './__generated__/usePopularFeedWithTimeGranularityStorageQuery.graphql.ts';

export const usePopularFeedWithTimeGranularityStorageQuery: GraphQLTaggedNode = graphql`
	query usePopularFeedWithTimeGranularityStorageQuery(
		$first: Int
		$after: String
		$timeGranularity: String = "default"
	) {
		popularFeed(first: $first, after: $after, timeGranularity: $timeGranularity) {
			nodes {
				content {
					title
					id
					type
					subType
					emoji: properties(key: "emoji-title-published") {
						nodes {
							id
							value
						}
					}
					contentProperties {
						latest {
							generatedBy
						}
					}
					excerpt(length: 350)
					labels {
						nodes {
							name
						}
					}
					links {
						webui
					}
					space {
						alias
						id
						key
						name
					}
					history {
						createdDate
						createdBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
						}
						ownedBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
						}
					}
					metadata {
						lastModifiedDate
						comments {
							commentsCount
						}
						currentuser {
							favourited {
								isFavourite
							}
						}
					}
					version {
						friendlyWhen
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

export const usePopularFeedWithTimeGranularityStorage = (
	renderPopularFeed: boolean,
): {
	popularFeedTimeGranularity: PopularFeedTimeGranularityValue;
	setPopularFeedTimeGranularity: (timeGranularity: PopularFeedTimeGranularityValue) => void;
	popularFeedQuery: UseQueryInteropSupportedResult<
		usePopularFeedWithTimeGranularityStorageQuery$data,
		usePopularFeedWithTimeGranularityStorageQuery$variables
	>;
} => {
	const { isRovoLLMEnabled } = useSessionData();

	const { value, setValue, query } = useFeedDropdownStorage<
		usePopularFeedWithTimeGranularityStorageQuery$data,
		usePopularFeedWithTimeGranularityStorageQuery$variables,
		PopularFeedTimeGranularityValue
	>(
		{
			defaultValue: PopularFeedTimeGranularityValue.Default,
			sessionStorageKey: sessionStorageKeys.HOME_POPULAR_FEED_TIME_GRANULARITY,
			// Skip vanilla V1 when the V2 query is going to fire for this user.
			// Otherwise vanilla V1 is the safe fallback that must always run.
			//
			// V2 fires when the user is Rovo-enabled; everyone else gets
			// vanilla V1.
			skipQuery: !renderPopularFeed || isRovoLLMEnabled,
		},
		{
			query: usePopularFeedWithTimeGranularityStorageQuery,
			variables: (
				timeGranularity: PopularFeedTimeGranularityValue,
			): usePopularFeedWithTimeGranularityStorageQuery$variables => ({
				timeGranularity,
			}),
		},
	);
	// V2 path: uses aiProperty directly on PopularFeedItem (parallel with Confluence hop).
	// Active when the user has Rovo LLM enabled — AI snippets only render for
	// Rovo-enabled users, so non-Rovo users fall back to the vanilla V1 query.
	const {
		value: valueWithSnippetsV2,
		setValue: setValueWithSnippetsV2,
		query: queryWithSnippetsV2,
	} = useFeedDropdownStorage<
		PopularFeedWithTimeGranularityStorageSnippetsV2Query$data,
		PopularFeedWithTimeGranularityStorageSnippetsV2Query$variables,
		PopularFeedTimeGranularityValue
	>(
		{
			defaultValue: PopularFeedTimeGranularityValue.Default,
			sessionStorageKey: sessionStorageKeys.HOME_POPULAR_FEED_TIME_GRANULARITY,
			skipQuery: !renderPopularFeed || !isRovoLLMEnabled,
			// Use the AGG (interop) client — V2 goes through AGG.
			interopApolloAggClient: true,
		},
		{
			query: PopularFeedWithTimeGranularityStorageSnippetsV2Query,
			variables: (
				timeGranularity: PopularFeedTimeGranularityValue,
			): PopularFeedWithTimeGranularityStorageSnippetsV2Query$variables => ({
				timeGranularity,
				// The V2 `skipQuery` above already gates on `!isRovoLLMEnabled`,
				// so this variables function is only ever invoked when Rovo LLM
				// is enabled. Pass `true` unconditionally — avoids dead-code
				// branching. This mirrors how `preloadFeed.ts` already handles
				// this (also passes `true` unconditionally in the V2 branch).
				isRovoLLMEnabled: true,
			}),
		},
	);

	return {
		popularFeedTimeGranularity: isRovoLLMEnabled ? valueWithSnippetsV2 : value,
		setPopularFeedTimeGranularity: isRovoLLMEnabled ? setValueWithSnippetsV2 : setValue,
		// Cast required: V2 query has a structurally compatible shape (the
		// `aiProperty` field is additive on PopularFeedItem) but TS can't infer
		// the union assignability for legacy callers that expect the V1 type.
		// Safe at runtime because PopularFeedCard reads only `content`/`id`.
		popularFeedQuery: (isRovoLLMEnabled
			? queryWithSnippetsV2
			: query) as UseQueryInteropSupportedResult<
			usePopularFeedWithTimeGranularityStorageQuery$data,
			usePopularFeedWithTimeGranularityStorageQuery$variables
		>,
	};
};
